import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface Props {
  groups: FilterGroup[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  summary: string;
}

export default function FilterPanel({ groups, values, onChange, summary }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="lg:hidden w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-background-200 bg-background-50 cursor-pointer"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-[13px] text-foreground-800 min-w-0">
          <i className="ri-filter-3-line text-accent-600" />
          <span className="font-medium">筛选条件</span>
          <span className="text-foreground-400 truncate">· {summary}</span>
        </span>
        <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div className={`${open ? "block" : "hidden"} lg:block mt-3 lg:mt-0`}>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="hidden lg:flex items-center gap-2 px-4 py-3 border-b border-background-200 bg-background-100">
            <i className="ri-filter-3-line text-accent-600" />
            <h2 className="font-heading text-[14px] font-bold text-foreground-900">筛选内容</h2>
          </div>
          <div className="p-4 space-y-4">
            {groups.map((group) => (
              <div key={group.key}>
                <p className="text-[12px] text-foreground-500 mb-2">{group.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {group.options.map((option) => {
                    const active = values[group.key] === option.value;
                    return (
                      <button
                        key={`${group.key}-${option.value}`}
                        type="button"
                        onClick={() => onChange(group.key, option.value)}
                        className={`px-2.5 py-1.5 rounded-md text-[12.5px] whitespace-nowrap transition-colors cursor-pointer border ${
                          active
                            ? "bg-primary-500 text-background-50 border-primary-500"
                            : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300 hover:text-primary-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                groups.forEach((group) => onChange(group.key, group.options[0].value));
              }}
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-background-300 text-[12.5px] text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置全部筛选
            </button>
          </div>
        </div>

        <div className="hidden lg:block mt-4 rounded-lg border border-background-200 bg-accent-50 p-4">
          <h3 className="font-heading text-[14px] font-bold text-primary-900 flex items-center gap-2">
            <i className="ri-lightbulb-line text-accent-600" />
            筛选小提示
          </h3>
          <p className="text-[12px] text-foreground-600 leading-6 mt-2">
            先按人物筛选，再叠加事件类型，能更快定位你关心的那条时间线。筛选项可随时重置。
          </p>
        </div>
      </div>
    </div>
  );
}